import { useEffect, useState } from "react";

/**
 * Date input that always DISPLAYS values as DD/MM/YYYY, regardless of
 * the user's browser locale. Internally the value is the canonical
 * ISO YYYY-MM-DD that callers pass in / receive via onChange.
 *
 * UI: a text field showing DD/MM/YYYY + a 📅 button next to it that
 * pops open the native date picker. Validation happens on blur — text
 * typing doesn't surface errors mid-stream.
 *
 * iOS/iPadOS Safari refuses to open the native date picker for inputs
 * hidden via `sr-only` or `display:none`, and only honours
 * `showPicker()` on actually-visible inputs. We work around that by
 * overlaying a real `<input type="date">` on top of the calendar
 * button with `opacity:0` — the input is visible to the layout engine
 * (so the picker opens) but invisible to the user (so the emoji
 * button is what they see).
 *
 * Project-wide convention: human-readable dates are ALWAYS DD/MM/YYYY
 * in the AltaJet Vuelos UI (Chile reads dd-mm-yyyy). Never expose the
 * native locale-dependent date input format directly.
 */
export function DateField({
  value,
  onChange,
  min,
  required,
  className,
}: {
  /** Canonical YYYY-MM-DD or "" when unset. */
  value: string;
  onChange: (next: string) => void;
  /** Min selectable date in YYYY-MM-DD. */
  min?: string;
  required?: boolean;
  className?: string;
}) {
  const [display, setDisplay] = useState<string>(
    value ? isoToDdmmyyyy(value) : ""
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDisplay(value ? isoToDdmmyyyy(value) : "");
    setError(null);
  }, [value]);

  function onTextChange(v: string) {
    setDisplay(v);
    const parsed = ddmmyyyyToIso(v);
    if (parsed) {
      onChange(parsed);
      setError(null);
    }
  }

  function onTextBlur() {
    if (!display) {
      onChange("");
      setError(required ? "Fecha requerida" : null);
      return;
    }
    const parsed = ddmmyyyyToIso(display);
    if (!parsed) {
      setError("Usa el formato DD/MM/YYYY");
      return;
    }
    if (min && parsed < min) {
      setError("Fecha en el pasado");
      return;
    }
    onChange(parsed);
    setDisplay(isoToDdmmyyyy(parsed));
    setError(null);
  }

  function onNativeChange(v: string) {
    onChange(v);
    setDisplay(isoToDdmmyyyy(v));
    setError(null);
  }

  return (
    <div className={className}>
      <div className="flex items-stretch gap-2">
        <input
          type="text"
          inputMode="numeric"
          value={display}
          onChange={(e) => onTextChange(e.target.value)}
          onBlur={onTextBlur}
          placeholder="DD/MM/YYYY"
          maxLength={10}
          aria-invalid={error ? "true" : undefined}
          className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
        />
        <div className="relative">
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="pointer-events-none rounded-md border border-slate-300 px-3 py-2 text-sm"
          >
            📅
          </button>
          <input
            type="date"
            value={value}
            min={min}
            onChange={(e) => onNativeChange(e.target.value)}
            aria-label="Abrir calendario"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </div>
      </div>
      {error ? <p className="mt-1 text-xs text-rose-600">{error}</p> : null}
    </div>
  );
}

function isoToDdmmyyyy(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function ddmmyyyyToIso(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (!m) return null;
  const dd = m[1]!.padStart(2, "0");
  const mm = m[2]!.padStart(2, "0");
  const yyyy = m[3]!;
  // Reject impossible dates like 31/02 or 32/13.
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`);
  if (
    d.getUTCFullYear() !== Number(yyyy) ||
    d.getUTCMonth() + 1 !== Number(mm) ||
    d.getUTCDate() !== Number(dd)
  ) {
    return null;
  }
  return `${yyyy}-${mm}-${dd}`;
}
